import { AfterViewInit, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { CatalogCrudModalComponent } from '../shared/components/catalog-crud-modal';
import { CatalogDeleteConfirmModalComponent } from '../shared/components/catalog-delete-confirm-modal';
import { CatalogPageCardComponent } from '../shared/components/catalog-page-card';
import { CatalogTableToolbarComponent } from '../shared/components/catalog-table-toolbar';
import { CatalogDatatableController, DatatableViewStateSnapshot } from '../shared/catalog-datatable.controller';
import { CatalogModalController } from '../shared/catalog-modal.controller';
import { formatCompactOrIsoDate } from '../../shared/trade-formatters';
import { FridaConfigService } from './frida-config.service';
import { FridaConfig, UpsertFridaConfigRequest } from './frida-config.types';
import { isConflictError, resolveApiErrorMessage } from '../../shared/api-error';

interface FridaConfigFormValue {
  name: string;
  value: string;
}

type FridaConfigFormControlName = keyof FridaConfigFormValue;

@Component({
  selector: 'app-frida-config',
  imports: [
    ReactiveFormsModule,
    CatalogPageCardComponent,
    CatalogTableToolbarComponent,
    CatalogCrudModalComponent,
    CatalogDeleteConfirmModalComponent,
  ],
  templateUrl: './frida-config.html',
  styleUrl: './frida-config.css',
})
export class FridaConfigComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly nameMaxLength = 50;
  protected readonly valueMaxLength = 500;
  private readonly datatableId = 'kt_frida_config_datatable';
  private readonly createModalId = 'create_frida_config_modal';
  private readonly editModalId = 'edit_frida_config_modal';
  private readonly deleteConfirmModalId = 'delete_frida_config_confirm_modal';
  private readonly formBuilder = inject(FormBuilder);
  private readonly datatableController = new CatalogDatatableController(this.datatableId);
  private readonly modalController = new CatalogModalController();

  private createModalObserver?: MutationObserver;
  private editModalObserver?: MutationObserver;

  protected readonly createForm = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
    value: ['', [Validators.maxLength(this.valueMaxLength)]],
  });
  protected readonly editForm = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
    value: ['', [Validators.maxLength(this.valueMaxLength)]],
  });
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly rows = signal<FridaConfig[]>([]);
  protected readonly isCreating = signal(false);
  protected readonly createFormSubmitted = signal(false);
  protected readonly createFormError = signal('');
  protected readonly editingName = signal<string | null>(null);
  protected readonly isUpdating = signal(false);
  protected readonly editFormSubmitted = signal(false);
  protected readonly editFormError = signal('');
  protected readonly deletingId = signal<number | null>(null);
  protected readonly pendingDeleteId = signal<number | null>(null);
  protected readonly alertVariant = signal<'success' | 'destructive' | null>(null);
  protected readonly alertTitle = signal('');
  protected readonly alertMessage = signal('');
  protected readonly hasAlert = computed(() => this.alertVariant() !== null);

  constructor(private readonly fridaConfigService: FridaConfigService) {}

  ngOnInit(): void {
    this.loadRows();
  }

  ngAfterViewInit(): void {
    this.bindCreateModalCloseReset();
    this.bindEditModalCloseReset();
  }

  ngOnDestroy(): void {
    this.createModalObserver?.disconnect();
    this.editModalObserver?.disconnect();
  }

  protected shouldShowCreateFieldError(controlName: FridaConfigFormControlName): boolean {
    return this.createFormSubmitted() && this.createForm.controls[controlName].invalid;
  }

  protected shouldShowEditFieldError(controlName: FridaConfigFormControlName): boolean {
    return this.editFormSubmitted() && this.editForm.controls[controlName].invalid;
  }

  protected shouldShowCreateRequiredError(controlName: FridaConfigFormControlName): boolean {
    const control = this.createForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowCreateMaxLengthError(controlName: FridaConfigFormControlName): boolean {
    const control = this.createForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected shouldShowEditRequiredError(controlName: FridaConfigFormControlName): boolean {
    const control = this.editForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowEditMaxLengthError(controlName: FridaConfigFormControlName): boolean {
    const control = this.editForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected isCreateValueDate(): boolean {
    return this.isDateField(this.createForm.controls.name.value);
  }

  protected isEditValueDate(): boolean {
    return this.isDateField(this.editForm.controls.name.value);
  }

  protected getCreateDatePickerValue(): string {
    return this.toIsoDate(this.createForm.controls.value.value);
  }

  protected getEditDatePickerValue(): string {
    return this.toIsoDate(this.editForm.controls.value.value);
  }

  protected onCreateDatePicked(event: Event): void {
    this.applyPickedDate(this.createForm.controls.value, event);
  }

  protected onEditDatePicked(event: Event): void {
    this.applyPickedDate(this.editForm.controls.value, event);
  }

  protected formatValueForTable(item: FridaConfig): string {
    const value = item.value.trim();
    if (!value) {
      return '-';
    }

    if (!this.isDateField(item.name) || !this.isDateLike(value)) {
      return value;
    }

    return formatCompactOrIsoDate(value);
  }

  protected loadRows(snapshot?: DatatableViewStateSnapshot): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.fridaConfigService.getFridaConfigs().subscribe({
      next: (rows) => {
        this.rows.set(rows);
        this.isLoading.set(false);
        this.datatableController.initialize(snapshot);
      },
      error: () => {
        this.errorMessage.set('Unable to load frida config data from the API.');
        this.isLoading.set(false);
      },
    });
  }

  protected onCreate(): void {
    this.createFormSubmitted.set(true);
    this.createFormError.set('');

    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }

    const payload = this.toPayload(this.createForm.getRawValue());

    this.isCreating.set(true);

    this.fridaConfigService.createFridaConfig(payload).subscribe({
      next: () => {
        this.isCreating.set(false);
        this.closeCreateModal();
        this.resetCreateForm();
        window.KTToast?.show({
          message: 'Frida config created successfully.',
          variant: 'success',
        });
        this.loadRows();
      },
      error: (error: unknown) => {
        this.isCreating.set(false);
        this.createFormError.set(isConflictError(error)
          ? resolveApiErrorMessage('A frida config with this name already exists.', error)
          : 'Unable to create frida config at this time. Please try again.');
      },
    });
  }

  protected resetCreateForm(): void {
    this.createForm.reset(this.getEmptyFormValue());
    this.createFormSubmitted.set(false);
    this.createFormError.set('');
  }

  protected openEditModal(row: FridaConfig): void {
    this.editingName.set(row.name);
    this.editForm.reset({
      name: row.name,
      value: row.value,
    });
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
    this.modalController.open(this.editModalId);
  }

  protected onUpdate(): void {
    this.editFormSubmitted.set(true);
    this.editFormError.set('');

    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    const name = this.editingName();
    if (!name) {
      this.editFormError.set('Unable to identify the frida config to update.');
      return;
    }

    const payload = this.toPayload(this.editForm.getRawValue());
    const datatableSnapshot = this.datatableController.captureViewState();

    this.isUpdating.set(true);

    this.fridaConfigService.updateFridaConfig(name, payload).subscribe({
      next: () => {
        this.isUpdating.set(false);
        this.closeEditModal();
        this.resetEditForm();
        window.KTToast?.show({
          message: 'Frida config updated successfully.',
          variant: 'success',
        });
        this.loadRows(datatableSnapshot);
      },
      error: () => {
        this.isUpdating.set(false);
        this.editFormError.set('Unable to update frida config at this time. Please try again.');
      },
    });
  }

  protected resetEditForm(): void {
    this.editForm.reset(this.getEmptyFormValue());
    this.editingName.set(null);
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
  }

  protected onTableActionClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const editButton = target?.closest('button[data-action="edit-frida-config"]') as HTMLButtonElement | null;
    const deleteButton = target?.closest('button[data-action="delete-frida-config"]') as HTMLButtonElement | null;

    if (editButton && !editButton.disabled) {
      const rowId = this.getRowIdFromButton(editButton);
      if (rowId === null) {
        return;
      }

      const row = this.rows().find((item) => item.id === rowId);
      if (!row) {
        return;
      }

      this.openEditModal(row);
      return;
    }

    if (!deleteButton || deleteButton.disabled) {
      return;
    }

    const rowId = this.getRowIdFromButton(deleteButton);
    if (rowId === null) {
      return;
    }

    this.openDeleteConfirmationModal(rowId);
  }

  protected cancelDelete(): void {
    this.pendingDeleteId.set(null);
    this.closeDeleteConfirmationModal();
  }

  protected confirmDelete(): void {
    const rowId = this.pendingDeleteId();

    if (rowId === null || this.deletingId() !== null) {
      return;
    }

    this.closeDeleteConfirmationModal();
    this.pendingDeleteId.set(null);
    this.removeRow(rowId);
  }

  protected removeRow(rowId: number): void {
    if (this.deletingId() !== null) {
      return;
    }

    this.deletingId.set(rowId);

    this.fridaConfigService.deleteFridaConfig(rowId).subscribe({
      next: (result) => {
        this.deletingId.set(null);

        if (result.kind === 'success') {
          const snapshot = this.datatableController.captureViewState();
          this.rows.update((rows) => rows.filter((item) => item.id !== rowId));
          this.showSuccessAlert('Frida config deleted successfully.');
          this.datatableController.initialize(snapshot);
          return;
        }

        this.showDestructiveAlert(
          'Unable to delete frida config.',
          result.message,
        );
      },
      error: () => {
        this.deletingId.set(null);
        this.showDestructiveAlert(
          'Unable to delete frida config.',
          'Unable to delete frida config at this time. Please try again.',
        );
      },
    });
  }

  protected dismissAlert(): void {
    this.alertVariant.set(null);
    this.alertTitle.set('');
    this.alertMessage.set('');
  }

  private bindCreateModalCloseReset(): void {
    this.createModalObserver = this.modalController.observeClose(this.createModalId, () => {
      this.resetCreateForm();
    });
  }

  private bindEditModalCloseReset(): void {
    this.editModalObserver = this.modalController.observeClose(this.editModalId, () => {
      this.resetEditForm();
    });
  }

  private closeCreateModal(): void {
    this.modalController.close(this.createModalId);
  }

  private closeEditModal(): void {
    this.modalController.close(this.editModalId);
  }

  private openDeleteConfirmationModal(rowId: number): void {
    this.pendingDeleteId.set(rowId);
    this.modalController.open(this.deleteConfirmModalId);
  }

  private closeDeleteConfirmationModal(): void {
    this.modalController.close(this.deleteConfirmModalId);
  }

  private showSuccessAlert(message: string): void {
    this.alertVariant.set('success');
    this.alertTitle.set(message);
    this.alertMessage.set('');
  }

  private showDestructiveAlert(title: string, message: string): void {
    this.alertVariant.set('destructive');
    this.alertTitle.set(title);
    this.alertMessage.set(message);
  }

  private getRowIdFromButton(button: HTMLButtonElement): number | null {
    const rowIdRaw = button.getAttribute('data-frida-config-id');
    const rowId = rowIdRaw ? Number(rowIdRaw) : Number.NaN;

    return Number.isFinite(rowId) ? rowId : null;
  }

  private getEmptyFormValue(): FridaConfigFormValue {
    return {
      name: '',
      value: '',
    };
  }

  private toPayload(rawValue: FridaConfigFormValue): UpsertFridaConfigRequest {
    return {
      name: rawValue.name.trim(),
      value: this.toNullableText(rawValue.value),
    };
  }

  private toNullableText(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private applyPickedDate(control: { setValue: (value: string) => void }, event: Event): void {
    const pickedValue = (event.target as HTMLInputElement | null)?.value ?? '';
    if (!pickedValue) {
      control.setValue('');
      return;
    }

    const compactDate = this.toCompactDate(pickedValue);
    if (!compactDate) {
      return;
    }

    control.setValue(compactDate);
  }

  private toIsoDate(value: string): string {
    const compactMatch = /^(\d{4})(\d{2})(\d{2})$/.exec(value.trim());
    if (!compactMatch) {
      return '';
    }

    const [, year, month, day] = compactMatch;
    return `${year}-${month}-${day}`;
  }

  private toCompactDate(value: string): string | null {
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!isoMatch) {
      return null;
    }

    const [, year, month, day] = isoMatch;
    return `${year}${month}${day}`;
  }

  private isDateField(name: string): boolean {
    return name.toLowerCase().includes('date');
  }

  private isDateLike(value: string): boolean {
    return /^\d{8}$/.test(value) || /^\d{4}-\d{2}-\d{2}/.test(value);
  }
}
